import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Modal,
  StyleSheet,
  Platform,
  Image
} from "react-native";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import CalenderContainer from "../../../../../../../../assets/images/Calendar-Container-2.png";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import  FloatingTextInputComponent from "../../../common/FloatingTextInputComponent";
import CloseIcon from "../../../../../../../../assets/images/Icon_close.png";


type Props = {
  onValidationChange?: (isValid: boolean) => void;
};

const BillPayDOBStep: React.FC = ({ onValidationChange }: Props) => {
  const { t } = useTranslation();
  const storedDobString = useSessionStore((s: SessionState) => (s as any).senderDOBString) || "";
  const [dob, setDob] = useState(storedDobString);
  const [showPicker, setShowPicker] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const setSenderDOBString = useSessionStore((s: SessionState) => (s as any).setSenderDOBString);
  const setSenderDOB = useSessionStore((s: SessionState) => (s as any).setSenderDOB);

  const isValid = dob.trim().length > 8;
  
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);
    
  // Keep local DOB in sync when store is hydrated (e.g., after AuthConsumer)
  useEffect(() => {
    setDob(storedDobString || "");
  }, [storedDobString]);

  // Mock ID verification function (replace with real API call)
  const verifyDOBWithID = async (enteredDob: string) => {
    console.log("Verifying DOB:", enteredDob);
    // Simulate backend check with previously uploaded ID
    return new Promise<boolean>((resolve) =>
      setTimeout(() => resolve(true), 1000) // always returns true for now
    );
  };

  const handleDateChange = async (_: any, selectedDate?: Date) => {
    setShowPicker(false);
    if (selectedDate) {
      const formatted = formatDate(selectedDate);
      setDob(formatted);

      // Call background verification
      const isValid = await verifyDOBWithID(formatted);
      if (isValid) {
        setShowConfirmModal(true);
      }
    }
  };

  const handleManualInput = async (rawText: string) => {
    const formatted = formatDobInput(rawText);
    setDob(formatted);
    try { setSenderDOBString?.(formatted); } catch {}
    // Check if valid mm/dd/yyyy format
    const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/(19|20)\d{2}$/;
    if (regex.test(formatted)) {
      const parsed = parseMMDDYYYY(formatted);
      try { setSenderDOB?.(parsed); } catch {}
      const isValid = await verifyDOBWithID(formatted);
      if (isValid) {
        setShowConfirmModal(true);
      }
    }
  };

  // Format partial input as MM/DD/YYYY while typing
  const formatDobInput = (raw: string): string => {
    const digits = (raw || "").replace(/\D/g, "").slice(0, 8);
    const mm = digits.slice(0, 2);
    const dd = digits.slice(2, 4);
    const yyyy = digits.slice(4, 8);
    if (digits.length <= 2) return mm;
    if (digits.length <= 4) return `${mm}/${dd}`;
    return `${mm}/${dd}/${yyyy}`;
  };

  const formatDate = (date: Date) => {
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  const parseMMDDYYYY = (text: string): Date => {
    const [mm, dd, yyyy] = text.split("/").map((p) => parseInt(p, 10));
    return new Date(yyyy, mm - 1, dd);
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t("SENDER_DOB_TITLE")}</Text>
      <Text style={styles.subtitle}>{t("SENDER_DOB_SUBTITLE")}</Text>

      <FloatingTextInputComponent
        isAutoFocus={true}
        label={"MM/DD/YYYY"}
        value={dob}
        onChangeText={handleManualInput}
      />
      {/* Bottom Popup Confirmation */}
      <View style={styles.infoBox}>
        <Text style={styles.infoText}>
          {t("YOUR_INFO_IS_ALWAYS_KEPT_PRIVATE")}
        </Text>
      </View>
      <Modal
        visible={showConfirmModal}
        animationType="slide"
        transparent={true}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.bottomSheet}>
            {/* <View style={styles.modalHeaderRow}> */}
              <TouchableOpacity style={styles.modalHeaderRow} onPress={() => setShowConfirmModal(false)}>
                <Image
                  source={CloseIcon}
                  style={{ width: 24, height: 24}}
                />
              </TouchableOpacity>
            {/* </View> */}
            <View style={{ alignItems: 'center' }}>
              <Image
                  source={CalenderContainer}
                  style={{ width: 64, height: 64, borderRadius: 50}}
                />
              <Text style={styles.modalTitle}>{t("SENDER_DOB_CONFIRMATION_TITLE")}</Text>
              <Text style={styles.modalInfo}>
                {t("SENDER_DOB_CONFIRM_SUBTITLE")}
              </Text>
              <Text style={styles.modalDOB}>{dob}</Text>
            </View>
              <View style={styles.footer}>
              <TouchableOpacity
                style={styles.confirmBtn}
                onPress={() => setShowConfirmModal(false)}
              >
                <Text style={[styles.cancelText, { color: Colors.white}]}>{t("SENDER_DOB_CONFIRM")}</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.cancelBtn}
                onPress={() => setShowConfirmModal(false)}
              >
                <Text style={styles.cancelText}>{t("EDIT")}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 24
  },
  inputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  input: { flex: 1, fontSize: 16 },
  modalOverlay: {
    flex: 1,
    justifyContent: "flex-end",
    backgroundColor: "rgba(0,0,0,0.4)",
  },
  modalHeaderRow: {
    alignSelf: 'flex-end',
    marginBottom: 48
  },
  modalTitle: {
    fontFamily: 'Rubik',
    fontSize: 24,
    fontWeight: "500",
    color: Colors.accountLock,
    lineHeight: 28,
    marginVertical: 26
  },
  modalInfo: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: "400",
    color: Colors.accountLock,
    lineHeight: 18,
    marginBottom: 26,
    textAlign: 'center',
  },
  bottomSheet: {
    backgroundColor: Colors.white,
    padding: 24,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  modalDOB: {
    fontFamily: 'Rubik',
    fontSize: 32,
    fontWeight: "500",
    color: Colors.accountLock,
    // marginTop: 10,
    marginBottom: 48,
  },
  confirmBtn: {
    backgroundColor: "#000",
    padding: 14,
    borderRadius: 8,
    alignItems: "center",
    height: 48
  },
  cancelBtn: { paddingTop: 24, alignItems: "center" },
  cancelText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: "500",
    color: Colors.darkGrey,
    lineHeight: 24,
  },
  infoBox: {
    marginTop: 20,
    padding: 12,
    borderWidth: 1,
    borderColor: "#FFF",
    borderRadius: 8,
  },
  infoText: { fontSize: 14, color: "#FFF" },
  footer: {
    // paddingHorizontal: 24,
    paddingBottom: Platform.OS === "ios" ? 24 : 16,
    backgroundColor: Colors.white
  },
});

export default BillPayDOBStep;